//src/components/ToolSelector.tsx
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Check, AlertCircle, Search, Code, LineChart, BrainCircuit, ChevronDown, ChevronUp } from 'lucide-react';
import { Cpu, Folder, Terminal, Database, History, Globe, Network, Bot } from 'lucide-react';
import { FileIcon, FileText, Image as ImageIcon, Music, Video, File } from 'lucide-react';
import { Alert } from '@/components/markdown/Alert';
import { Highlight } from '@/components/markdown/Highlight';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Plot } from '@/components/markdown/Plot';
import { Tooltip } from '@/components/markdown/Tooltip';
import type { CustomComponent } from '@/components/markdown/types';
import type { RechartsPlotData, RechartsPlotLayout } from '@/components/markdown/Plot';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import ToolConfigurator, { ToolConfig } from './ToolConfigurator';
import { useToast } from '@/components/hooks/use-toast';
import GoogleDemo from "@/components/demos/GoogleDemo";
import CodeExecutionDemo from '@/components/demos/CodeExecutionDemo';
import NetworkGraphDemo from '@/components/demos/NetworkGraphDemo';
import FileBrowserDemo from '@/components/demos/FileBrowserDemo';
import VertexSearchDemo from '@/components/demos/VertexSearchDemo';
import VertexDataStoreDemo from '@/components/demos/VertexDataStoreDemo';
import { getAuth } from 'firebase/auth';
import FirebaseService, { type Tag } from '@/lib/firebase';
import { checkTagAccess } from '@/lib/access-control';
import { filterConfigFieldsByTags, TAG_RESTRICTED_CONFIGS } from '@/lib/tag-config-utils';
import type { TagAwareConfigField, TagRestrictedOption } from '@/types/tag-config';
import type { AssistantDisplay } from '@/types';

export interface Tool {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.FC<{ className?: string }>;
  component?: CustomComponent;
  demo: React.ComponentType;
  configFields?: TagAwareConfigField[];
  tags?: string[]; // Tags for categorization and access control
}

interface ToolSelectorProps {
  selectedTools: string[];
  onChange: (tools: string[]) => void;
  toolConfigs?: Record<string, Record<string, any>>; 
  onConfigChange?: (toolId: string, config: Record<string, any>) => void; 
  defaultTools?: string[];
  defaultOpen?: boolean;
  availableAssistants?: AssistantDisplay[]; // Optional list of assistants for assistant selector
}

export const AVAILABLE_TOOLS: Tool[] = [
    {
        id: 'preview',
        name: 'Content Preview',
        description: 'Preview documents and media files',
        category: 'Output',
        icon: ImageIcon,
        demo: () => (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="border rounded p-2">
                <div className="text-xs text-muted-foreground mb-1">PDF</div>
                <div className="bg-muted aspect-[3/4] rounded flex items-center justify-center">
                  <FileText className="h-8 w-8 text-muted-foreground"/>
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="text-xs text-muted-foreground mb-1">Image</div>
                <div className="bg-muted aspect-[3/4] rounded flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-muted-foreground"/>
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="text-xs text-muted-foreground mb-1">Video</div>
                <div className="bg-muted aspect-[3/4] rounded flex items-center justify-center">
                  <Video className="h-8 w-8 text-muted-foreground"/>
                </div>
              </div>
            </div>
            <Alert>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
                <span>Preview system ready</span>
              </div>
            </Alert>
          </div>
        )
     },
     {
      id: 'vertex_search',
      name: 'Data Store Search',
      description: 'Vertex AI Search DataStores',
      category: 'Data Sources',
      icon: Search,
      demo: VertexDataStoreDemo,
      configFields: [
        {
          key: 'datastore_id',
          label: 'Data Store ID from Vertex AI Search',
          type: 'select',
          placeholder: 'Select a data store',
          options: [
            { label: 'Aitana Public Welcome', value: 'aitana_public_welcome' },
            { label: 'Our New Energy Documents', value: 'aitana3', tags: ['admin-tools', 'ONE'] },
            { label: 'Aitana Labs Documentation', value: 'aitana-assistant-docs', tags: ['dev-team', 'admin-tools']},
            { label: 'Custom Data Store', value: 'custom' }
          ] as TagRestrictedOption[],
          required: true
        } as TagAwareConfigField,
        {
          key: 'customDatastoreId',
          label: 'Custom Data Store ID (if Custom selected above)',
          type: 'text',
          placeholder: 'e.g. my-custom-datastore',
          required: false
        },
        {
          key: 'datastore_filter',
          label: 'Optional Filter for Data Store',
          type: 'text',
          placeholder: 'vector_name: ANY("aitana3")',
          required: false
        },  
        {
          key: 'hardcode-query',
          label: 'Hardcoded Query',
          type: 'text',
          placeholder: 'Always use this query when searching',
          required: false
        },  
        {
          key: 'no-stream',
          label: 'Disable streaming',
          type: 'checkbox',
          placeholder: 'Disable intermediate answers',
          required: false
        }      
      ] as TagAwareConfigField[]
    },
    {
      id: 'file-browser',
      name: 'File Browser',
      description: 'Select files from cloud storage',
      category: 'Data Sources',
      icon: Folder,
      demo: FileBrowserDemo,
      configFields: [
        {
          key: 'bucketUrl',
          label: 'Storage Bucket - sa-emissary@aitana-multivac-production.iam.gserviceaccount.com must have access',
          type: 'select',
          placeholder: 'Select a storage bucket',
          options: [
            { label: 'Aitana Public Bucket', value: 'aitana-public-bucket' },
            { label: 'Aitana Documents Bucket', value: 'aitana-documents-bucket', tags: ['admin-tools', 'ONE'] },
            { label: 'Aitana BigQuery Bucket', value: 'aitana-bigquery-bucket', tags: ['admin-tools', 'ONE'] },
            { label: 'Custom Bucket', value: 'custom' }
          ] as TagRestrictedOption[],
          required: true
        } as TagAwareConfigField,
        {
          key: 'customBucketUrl',
          label: 'Custom Bucket URL (if Custom selected above)',
          type: 'text',
          placeholder: 'gs://your-custom-bucket-name',
          required: false
        },
        {
          key: 'rootPath',
          label: 'Root Path',
          type: 'text',
          placeholder: '/',
          required: false
        },
        {
          key: 'no-stream',
          label: 'Disable streaming',
          type: 'checkbox',
          placeholder: 'Disable intermediate answers',
          required: false
        }
      ]
     },
    {
      id: 'document_search_agent',
      name: 'Document Search Agent',
      description: 'AI-powered document discovery and extraction',
      category: 'Data Sources',
      icon: Bot,
      tags: ['admin-tools', 'beta-testers'], 
      demo: () => (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <Bot className="h-4 w-4"/>
            <span className="font-medium">Agent-based Document Search</span>
          </div>
          <div className="space-y-2">
            <div className="border rounded-lg p-3">
              <div className="text-xs font-medium mb-1">🔍 Phase 1: Discovery</div>
              <div className="text-xs text-muted-foreground">AI search identifies relevant documents</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs font-medium mb-1">📄 Phase 2: Extraction</div>
              <div className="text-xs text-muted-foreground">File browser retrieves content</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs font-medium mb-1">✅ Phase 3: Assessment</div>
              <div className="text-xs text-muted-foreground">Gemini Flash evaluates completeness</div>
            </div>
          </div>
          <Alert>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"/>
              <span>Agent system ready</span>
            </div>
          </Alert>
        </div>
      ),
      configFields: [
        {
          key: 'preset_config',
          label: 'Data Source Configuration',
          type: 'select',
          placeholder: 'Select a preset configuration',
          options: [
            ...TAG_RESTRICTED_CONFIGS.documentSearchAgent.presets,
            { label: 'Custom Configuration', value: 'custom' }
          ],
          required: true
        },
        {
          key: 'bucketUrl',
          label: 'Storage Bucket (Custom only)',
          type: 'text',
          placeholder: 'your-custom-bucket-name',
          required: false
        },
        {
          key: 'datastore_id',
          label: 'Data Store ID (Custom only)',
          type: 'text',
          placeholder: 'e.g. my-custom-datastore',
          required: false
        },
        {
          key: 'max_iterations',
          label: 'Maximum Search Iterations',
          type: 'select',
          placeholder: 'Select iteration limit',
          options: [
            { label: '2 iterations', value: '2' },
            { label: '3 iterations', value: '3' },
            { label: '5 iterations', value: '5' }
          ],
          required: false
        },
        {
          key: 'max_documents_per_iteration',
          label: 'Documents Per Iteration',
          type: 'select',
          placeholder: 'Select document limit',
          options: [
            { label: '3 documents', value: '3' },
            { label: '5 documents', value: '5' },
            { label: '10 documents', value: '10' }
          ],
          required: false
        },
        {
          key: 'no-stream',
          label: 'Disable streaming',
          type: 'checkbox',
          placeholder: 'Disable intermediate answers',
          required: false
        }
      ] as TagAwareConfigField[]
    },
    {
      id: 'vertex_search_app',
      name: 'Knowledge Search',
      description: 'Vertex AI Search Apps in Sidebar',
      category: 'Data Sources',
      icon: Search,
      demo: VertexSearchDemo,
      configFields: [
        {
          key: 'configId',
          label: 'Knowledge Base Configuration',
          type: 'select',
          placeholder: 'Select a knowledge base',
          options: [
            { label: 'Our New Energy Documents', value: '4199968a-56e9-436e-b8ea-566bbdc50445', tags: ['ONE', 'admin-tools'] },
            { label: 'Aitana Google Workspace', value: '800734af-6206-43b4-be90-b32bce088b96' , tags: ['admin-tools']},
            { label: 'Aitana Public Demo', value: 'c09df02f-06e3-4285-b62f-823dcb95407d' },
            { label: 'Aitana Global Chat History', value: '24f3bd0e-0834-486b-8081-172ecc997711', tags: ['admin-tools']},
            { label: 'Aitana Labs Documentation', value: 'f806c8ef-cb0f-4c6f-b756-6318b3fabcd6', tags: ['dev-team', 'admin-tools']},
            { label: 'Custom Configuration', value: 'custom' }
          ] as TagRestrictedOption[],
          required: true
        } as TagAwareConfigField,
        {
          key: 'customConfigId',
          label: 'Custom Config ID (if Custom selected above)',
          type: 'text',
          placeholder: 'e.g. c09df02f-06e3-4285-b62f-823dcb95407d',
          required: false
        },
        {
          key: 'placeholder',
          label: 'Search Placeholder Text',
          type: 'text',
          placeholder: 'Search and ask knowledge base...',
          required: false
        },
        {
          key: 'no-stream',
          label: 'Disable streaming',
          type: 'checkbox',
          placeholder: 'Disable intermediate answers',
          required: false
        }
      ] as TagAwareConfigField[]
    },
    {
      id: 'vertex_search_first',
      name: 'Initial Data Store Search',
      description: 'Generic Search before first impression',
      category: 'Data Sources',
      icon: Search,
      demo: VertexDataStoreDemo,
      configFields: [
        {
          key: 'datastore_id',
          label: 'Data Store ID from Vertex AI Search',
          type: 'select',
          placeholder: 'Select a data store',
          options: [
            { label: 'Aitana Public Welcome', value: 'aitana_public_welcome' },
            { label: 'Aitana', value: 'aitana3', tags: ['admin-tools', 'ONE'] },
            { label: 'Aitana Labs Documentation', value: 'aitana-assistant-docs', tags: ['dev-team', 'admin-tools']},
            { label: 'Custom Data Store', value: 'custom' }
          ] as TagRestrictedOption[],
          required: true
        } as TagAwareConfigField,
        {
          key: 'datastore_filter',
          label: 'Optional Filter for Data Store',
          type: 'text',
          placeholder: 'vector_name: ANY("aitana3")',
          required: false
        },
        {
          key: 'customDatastoreId',
          label: 'Custom Data Store ID',
          type: 'text',
          placeholder: 'Enter your custom data store ID',
          required: false
        },  
        {
          key: 'hardcode-query',
          label: 'Hardcoded Query',
          type: 'text',
          placeholder: 'Always use this query when searching',
          required: false
        },      
      ] as TagAwareConfigField[]
    },
    {
      id: 'google_search_retrieval',
      name: 'Google Web Search',
      description: 'Real-time Google search access',
      category: 'Data Sources',
      icon: Search,
      demo: GoogleDemo,
      configFields: [
        {
          key: 'no-stream',
          label: 'Disable streaming',
          type: 'checkbox',
          placeholder: 'Disable intermediate answers',
          required: false
        },
        {
          key: 'hardcode-query',
          label: 'Hardcoded Query',
          type: 'text',
          placeholder: 'Always use this query when searching',
          required: false
        },  
      ] as TagAwareConfigField[]
    },
  {
    id: 'highlights',
    name: 'Text Highlights',
    description: 'Highlight key phrases in responses',
    category: 'Output',
    icon: AlertCircle,
    component: Highlight,
    demo: () => (
      <div className="space-y-2">
        <p>Example highlights:</p>
        <Highlight>Important information</Highlight>
        <Highlight color="#4caf50">Success highlight</Highlight>
        <Highlight color="#f44336">Error highlight</Highlight>
      </div>
    )
  },
  {
    id: 'plots',
    name: 'Data Visualization',
    description: 'Generate charts and graphs',
    category: 'Output',
    icon: LineChart,
    component: Plot,
    demo: () => {
        return (
          <div className="w-full h-[160px] p-3">
            <div className="text-xs text-muted-foreground mb-3">Sample Chart:</div>
            <div className="flex items-center justify-between">
              <div className="relative w-16 h-16">
                {/* Simple pie chart using overlapping circles */}
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-blue-500"></div>
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-green-500" 
                     style={{clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)'}}></div>
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-purple-500" 
                     style={{clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)'}}></div>
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-orange-500" 
                     style={{clipPath: 'polygon(50% 50%, 50% 100%, 0% 100%, 0% 75%)'}}></div>
              </div>
              <div className="flex-1 ml-3 space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span>Data A (40%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Data B (25%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span>Data C (25%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span>Data D (10%)</span>
                </div>
              </div>
            </div>
            <div className="text-xs text-center text-muted-foreground mt-2">
              Interactive charts with hover & export
            </div>
          </div>
        );
      }
  },
  {
    id: 'network',
    name: 'Network Graphs',
    description: 'Visualize relationships and workflows',
    category: 'Output',
    icon: Network, 
    demo: NetworkGraphDemo, 
  },
  {
    id: 'alerts',
    name: 'Alert Messages',
    description: 'Display important notifications',
    category: 'Output',
    icon: AlertCircle,
    component: Alert,
    demo: () => (
      <div className="space-y-2">
        <Alert>Default alert message</Alert>
        <Alert type="success">Success alert message</Alert>
        <Alert type="warning">Warning alert message</Alert>
        <Alert type="error">Error alert message</Alert>
      </div>
    )
  },
  {
    id: 'tooltips',
    name: 'Tooltips',
    description: 'Show contextual information',
    category: 'Output',
    icon: AlertCircle,
    component: Tooltip,
    demo: () => (
      <div className="p-4 space-y-4">
        <Tooltip text="This is a helpful tip">Hover over me</Tooltip><br/>
        <Tooltip text="More information" position="right">Right tooltip</Tooltip>
      </div>
    )
  },
  {
    id: 'code_execution',
    name: 'Code Execution',
    description: 'Execute code to help inform answers',
    category: 'Integration', 
    icon: Cpu,
    tags: ['beta-testers'], // Restricted to beta testers
    demo: CodeExecutionDemo,
    configFields: [
      {
        key: 'no-stream',
        label: 'Disable streaming',
        type: 'checkbox',
        placeholder: 'Disable intermediate answers',
        required: false
      },     
      {
        key: 'coder_model',
        label: 'Coder Model',
        type: 'select',
        options: [
          { label: 'Gemini', value: 'gemini' },
          { label: 'Anthropic', value: 'anthropic' }, 
        ], 
        required: false
      }
    ]
 },
 {
  id: 'user-history',
  name: 'Chat Memory',
  description: 'Access to your previous conversations',
  category: 'Data Sources',
  icon: History,
  configFields: [
    {
      key: 'no-stream',
      label: 'Disable streaming',
      type: 'checkbox',
      placeholder: 'Disable intermediate answers',
      required: false
    },     
  ],
  demo: () => (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <History className="h-4 w-4"/>
        <span>Recent Conversations</span>
      </div>
      <div className="space-y-2">
        {[
          { time: '2 hours ago', topic: 'Data Analysis Project Review' },
          { time: 'Yesterday', topic: 'Marketing Strategy Discussion' },
          { time: '3 days ago', topic: 'Technical Documentation Help' }
        ].map(item => (
          <div key={item.topic} className="bg-muted p-2 rounded-lg">
            <div className="font-medium">{item.topic}</div>
            <div className="text-xs text-muted-foreground">{item.time}</div>
          </div>
        ))}
      </div>
    </div>
  )
},
{
  id: 'domain-history',
  name: 'Company Memory',
  description: 'Access previous conversations across the company',
  category: 'Data Sources',
  icon: History,
  configFields: [
    {
      key: 'no-stream',
      label: 'Disable streaming',
      type: 'checkbox',
      placeholder: 'Disable intermediate answers',
      required: false
    },     
  ],
  demo: () => (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <History className="h-4 w-4"/>
        <span>Recent Company Conversations</span>
      </div>
      <div className="space-y-2">
        {[
          { time: '2 hours ago', topic: 'Data Analysis Project Review', user: 'fred', domain: 'company.com'},
          { time: 'Yesterday', topic: 'Marketing Strategy Discussion', user: 'amy', domain: 'company.com'},
          { time: '3 days ago', topic: 'Technical Documentation Help', user:'bob', domain: 'company.com'}
        ].map(item => (
          <div key={item.topic} className="bg-muted p-2 rounded-lg">
            <div className="font-medium">{item.user}@{item.domain}</div>
            <div className="font-medium">{item.topic}</div>
            <div className="text-xs text-muted-foreground">{item.time}</div>
          </div>
        ))}
      </div>
    </div>
  )
},
{
  id: 'global-history',
  name: 'Global Memory',
  description: 'Access global previous conversations',
  category: 'Data Sources',
  icon: History,
  tags: ['admin-tools'],
  configFields: [
    {
      key: 'no-stream',
      label: 'Disable streaming',
      type: 'checkbox',
      placeholder: 'Disable intermediate answers',
      required: false
    },     
  ],
  demo: () => (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <History className="h-4 w-4"/>
        <span>Recent Global Conversations</span>
      </div>
      <div className="space-y-2">
        {[
          { time: '2 hours ago', topic: 'Data Analysis Project Review', user: 'fred', domain:'companyA.com'},
          { time: 'Yesterday', topic: 'Marketing Strategy Discussion', user: 'amy', domain:'companyB.com'},
          { time: '3 days ago', topic: 'Technical Documentation Help', user:'bob', domain:'companyC.com' }
        ].map(item => (
          <div key={item.topic} className="bg-muted p-2 rounded-lg">
            <div className="font-medium">{item.user}@{item.domain}</div>
            <div className="font-medium">{item.topic}</div>
            <div className="text-xs text-muted-foreground">{item.time}</div>
          </div>
        ))}
      </div>
    </div>
  )
},
{
  id: 'only-echo',
  name: 'Echo',
  description: 'Will only echo',
  category: 'Behaviour',
  icon: Code, 
  tags: ['admin-tools'],
  demo: () => (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <History className="h-4 w-4"/>
        <span>Echo</span>
      </div>
      <div className="space-y-2">
        {[
          { topic: 'Hello, World', user: 'User'},
          { topic: 'Hello, World', user: 'Aitana'},
        ].map(item => (
          <div key={item.topic} className="bg-muted p-2 rounded-lg">
            <div className="font-medium">{item.user}</div>
            <div className="font-medium">{item.topic}</div>
          </div>
        ))}
      </div>
    </div>
  )
},
  {
    id: 'aitana-agent',
    name: 'Aitana Agents',
    description: 'Call other Aitana Assistants',
    category: 'Integration',
    tags: ['beta-testers', 'admin-tools'], // Requires either beta-testers OR admin-tools tag
    icon: Code,
    configFields: [
      {
        key: 'selected_assistants',
        label: 'Select Assistants to Call',
        type: 'assistant_multiselect',
        placeholder: 'Choose from your accessible assistants',
        required: true
      } as TagAwareConfigField,
      {
        key: 'call_strategy',
        label: 'Calling Strategy',
        type: 'radio',
        placeholder: 'Select how to call multiple assistants',
        defaultValue: 'parallel',
        options: [
          { label: 'Parallel (Default) - Call all assistants simultaneously', value: 'parallel' },
          { label: 'Sequential - Call assistants in order, sharing conversation history', value: 'sequential' }
        ],
        required: false
      } as TagAwareConfigField,
      {
        key: 'max_iterations',
        label: 'Maximum Recursion Depth',
        type: 'radio',
        placeholder: 'Limit recursive assistant calls',
        defaultValue: '1',
        options: [
          { label: '1 level (default) - no recursion', value: '1' },
          { label: '2 levels', value: '2' },
          { label: '3 levels', value: '3' },
          { label: '5 levels', value: '5', tags: ['beta-testers', 'admin-tools'] },
          { label: '10 levels', value: '10', tags: ['beta-testers', 'admin-tools'] },
          { label: 'Unlimited', value: '50', tags: ['admin-tools'] }
        ] as TagRestrictedOption[],
        required: false
      } as TagAwareConfigField
    ] as TagAwareConfigField[],
    demo: () => (
      <Alert>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span>Aitana Ready</span>
        </div>
      </Alert>
    )
  },
  {
    id: 'external_mcp',
    name: 'External MCP Server',
    description: 'Connect to external MCP services',
    category: 'Data Sources',
    icon: Database,
    tags: ['admin-tools'], // Only admin users can use MCP connections
    configFields: [
      {
        key: 'url',
        label: 'MCP Server URL',
        type: 'text',
        placeholder: 'https://api.example.com/mcp or with params: /mcp?api_key=xxx&profile=yyy',
        required: true
      },
      {
        key: 'auth',
        label: 'API Key (Optional - for Authorization header)',
        type: 'text',
        placeholder: 'Bearer token or API key (Note: Some servers like Browserbase need params in URL instead)',
        required: false
      },
      {
        key: 'max_iterations',
        label: 'Max Tool Iterations (1-50)',
        type: 'number',
        placeholder: 'Number of iterations (default: 10)',
        defaultValue: '10',
        min: 1,
        max: 50,
        required: false
      },
      {
        key: 'stream_intermediate',
        label: 'Stream Intermediate Results',
        type: 'checkbox',
        placeholder: 'Show progress between tool calls',
        defaultValue: true,
        required: false
      },
      {
        key: 'selected_tools',
        label: 'Available Tools',
        type: 'select',
        options: [] as TagRestrictedOption[],
        required: false,
        placeholder: 'Enter URL above to discover tools'
      }
    ],
    demo: () => (
      <div className="space-y-4">
        <div className="bg-black rounded-lg p-4 font-mono text-xs text-white">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <Database className="h-4 w-4"/>
            <span>Example: Multi-Tool Workflow</span>
          </div>
          <div className="text-green-400">🔧 Iteration 1: browserbase_session_create</div>
          <div className="text-blue-400">🔧 Iteration 2: browserbase_navigate(url="example.com")</div>
          <div className="text-yellow-400">🔧 Iteration 3: browserbase_extract(selector="h1")</div>
          <div className="text-purple-400">✅ Complete: "Found heading: Welcome to Example"</div>
          <div className="mt-2 text-gray-400">Supports 1-50 iterations (default: 10) for complex workflows</div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"/>
            <span>Multi-tool agent mode - perfect for complex workflows</span>
          </div>
        </Alert>
      </div>
    )
},

{
    id: 'mcp_demo',
    name: 'MCP Demo Tools',
    description: 'Demo MCP Server with test tools for end-to-end testing',
    category: 'Data Sources',
    icon: Database,
    tags: ['admin-tools'], // Admin only for testing
    configFields: [
      {
        key: 'server_id',
        label: 'MCP Server',
        type: 'select',
        options: [
          { label: 'Demo Server', value: 'demo' }
        ] as TagRestrictedOption[],
        defaultValue: 'demo',
        required: true
      },
      {
        key: 'tool',
        label: 'Demo Tool',
        type: 'select',
        options: [
          { label: 'Greeting', value: 'demo_greeting' },
          { label: 'Random Fact', value: 'demo_random_fact' },
          { label: 'Calculator', value: 'demo_calculator' },
          { label: 'Data Analyzer', value: 'demo_data_analyzer' },
          { label: 'Text Processor', value: 'demo_text_processor' },
          { label: 'System Info', value: 'demo_system_info' }
        ] as TagRestrictedOption[],
        required: false,
        placeholder: 'Select which demo tool to use'
      }
    ],
    demo: () => (
      <div className="space-y-4">
        <div className="bg-black rounded-lg p-4 font-mono text-xs text-white">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <Database className="h-4 w-4"/>
            <span>Demo MCP Server</span>
          </div>
          <div className="text-blue-400">&gt; demo_greeting("World", "es")</div>
          <div className="text-green-400">¡Hola World! Bienvenido al Servidor MCP Demo de Aitana.</div>
          <div className="mt-2 text-blue-400">&gt; demo_calculator("multiply", 7, 8)</div>
          <div className="text-green-400">{"{ result: 56, expression: '7 multiply 8 = 56' }"}</div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"/>
            <span>Test mode - Admin only</span>
          </div>
        </Alert>
      </div>
    )
},

{
    id: 'url_processing',
    name: 'Web URL Reader',
    description: 'Extract and analyze web content',
    category: 'Data Sources',
    icon: Globe,
    tags: ['admin-tools', 'beta-testers'],
    configFields: [
      {
        key: 'no-stream',
        label: 'Disable streaming',
        type: 'checkbox',
        placeholder: 'Disable intermediate answers',
        required: false
      },     
      {
        key: 'hardcode-url',
        label: 'Hardcoded URL',
        type: 'text',
        placeholder: 'https://example.com/article',
        required: false
      }
    ],
    demo: () => (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <Globe className="h-4 w-4"/>
          <div className="flex-1 truncate bg-muted px-2 py-1 rounded">
            https://example.com/article
          </div>
        </div>
        <div className="border rounded-lg p-3 space-y-2">
          <div className="font-medium">Extracted Content:</div>
          <div className="text-sm text-muted-foreground line-clamp-3">
            The article discusses key trends in artificial intelligence and their impact on business operations. 
            Main points include machine learning applications, natural language processing advancements, and...
          </div>
          <div className="text-xs text-muted-foreground">
            Last updated: 2 hours ago • Reading time: 5 min
          </div>
        </div>
      </div>
    )
},
{
  id: 'assistant-memory',
  name: 'Assistant Memory',
  description: 'Persistent memory across conversation thread (up to 10 files)',
  category: 'Data Sources',
  icon: Database,
  tags: ['beta-testers'],
  demo: () => (
    <div className="space-y-4">
      <div className="border rounded-lg p-3">
        <div className="font-medium mb-2">Memory Files</div>
        <div className="space-y-1 text-xs font-mono">
          <div className="flex items-center gap-2">
            <FileText className="h-3 w-3"/>
            <span>/user_preferences.txt</span>
            <span className="text-muted-foreground">(245 bytes)</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-3 w-3"/>
            <span>/project_context.md</span>
            <span className="text-muted-foreground">(1.2 KB)</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-3 w-3"/>
            <span>/important_dates.txt</span>
            <span className="text-muted-foreground">(89 bytes)</span>
          </div>
        </div>
      </div>
      <div className="bg-muted rounded-lg p-3 text-xs">
        <div className="font-medium mb-1">How it works:</div>
        <ul className="space-y-1 text-muted-foreground">
          <li>• Memory persists across sessions</li>
          <li>• Works with ALL AI models (Gemini, Claude, OpenAI)</li>
          <li>• Claude executor handles file operations</li>
          <li>• Stored securely in Firestore</li>
          <li>• Private to this assistant (max 10 files)</li>
        </ul>
      </div>
      <Alert>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"/>
          <span>Memory system ready - simply enable to use</span>
        </div>
      </Alert>
    </div>
  )
},
{
  id: 'no-first-impression',
  name: 'Suppress First Impression',
  description: 'Disables AI tool selection (search tools need hardcoded queries)',
  category: 'Behaviour',
  icon: BrainCircuit,
  demo: () => (
    <div className="space-y-3">
      <Alert>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"/>
          <span>First Impression suppressed</span>
        </div>
      </Alert>
      <Alert type="warning">
        <div className="text-sm">
          <strong>Incompatible with:</strong>
          <ul className="mt-1 ml-4 list-disc text-xs">
            <li>Chat Memory (all types)</li>
            <li>Code Execution</li>
          </ul>
          <strong className="mt-2 block">Requires hardcoded queries for:</strong>
          <ul className="mt-1 ml-4 list-disc text-xs">
            <li>Google Web Search</li>
            <li>Data Store Search</li>
          </ul>
        </div>
      </Alert>
    </div>
  )
},
{
  id: 'suppress-tool-streaming',
  name: 'Suppress Tool Streaming',
  description: 'Tools will not stream intermediate answers',
  category: 'Behaviour',
  icon: BrainCircuit,
  demo: () => (
    <div className="space-y-3">
      <Alert>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"/>
          <span>Tool Streaming suppressed</span>
        </div>
      </Alert>
    </div>
  )
},
 {
    id: 'advanced_models',
    name: 'Advanced Models',
    description: 'Access enhanced reasoning capabilities',
    category: 'Behaviour',
    icon: BrainCircuit,
    configFields: [
      {
        key: 'smart_tool',
        label: 'Pick Model',
        type: 'select',
        options: [
          {"label": "Anthropic 4.8 Opus", "value":"anthropic-4.8-opus", "tags": ['admin-tools']},
          {"label": "Anthropic 4.6 Sonnet", "value":"anthropic-4.6-sonnet"},
          {"label": "Anthropic 4.5 Haiku", "value":"anthropic-4.5-haiku"},
          {"label": "Gemini 2.5 Pro", "value": "gemini-2.5-pro"},
          {"label": "Gemini 2.5 Pro Thinking", "value": "gemini-2.5-pro-thinking", "tags": ['beta-testers']},
          {"label": "Gemini 2.5 Flash", "value": "gemini-2.5-flash"},
          {"label": "Gemini 2.5 Flash Thinking", "value": "gemini-2.5-flash-thinking", "tags": ['beta-testers']},
          {"label": "OpenAI GPT-5.5", "value": "openai-gpt-5", "tags": ['beta-testers']},
          {"label": "OpenAI GPT-5.4 Mini", "value": "openai-gpt-5-mini"},
          {"label": "OpenAI GPT-5.4 Nano", "value": "openai-gpt-5-nano"},
          {"label": "None", "value": "none"}
        ] as TagRestrictedOption[],
        required: false
      } as TagAwareConfigField,
      {
        key: 'enable_anthropic_web_search',
        label: 'Enable Web Search (Anthropic models only)',
        type: 'checkbox',
        placeholder: 'Enable built-in web search capabilities',
        required: false
      }
    ],
    demo: () => (
      <div className="space-y-3">
        <div className="bg-black rounded-lg p-3 font-mono text-xs">
          <div className="text-blue-400">Model: Gemini 2.5 / Claude 4.x / OpenAI GPT-5.5</div>
          <div className="text-green-400">Capabilities:</div>
          <div className="text-white ml-2">• Advanced reasoning</div>
          <div className="text-white ml-2">• Complex analysis</div>
          <div className="text-white ml-2">• Enhanced creativity</div>
          <div className="text-white ml-2">• Improved accuracy</div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"/>
            <span>Advanced model ready</span>
          </div>
        </Alert>
      </div>
    )
 },
 {
    id: 'dynamic-ui',
    name: 'Dynamic UI Components',
    description: 'Generate interactive UI elements dynamically',
    category: 'Output',
    icon: BrainCircuit,
    configFields: [
      {
        key: 'default_theme',
        label: 'Default Theme',
        type: 'select',
        options: [
          { label: 'Modern', value: 'modern' },
          { label: 'Minimal', value: 'minimal' },
          { label: 'Classic', value: 'classic' }
        ],
        required: false
      }
    ],
    demo: () => (
      <div className="space-y-4">
        <div className="border rounded-lg p-3">
          <div className="font-medium mb-2">Dynamic UI Preview</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-blue-100 p-2 rounded text-sm text-center">📊 Dashboard</div>
            <div className="bg-green-100 p-2 rounded text-sm text-center">📋 Form</div>
            <div className="bg-purple-100 p-2 rounded text-sm text-center">📈 Charts</div>
            <div className="bg-orange-100 p-2 rounded text-sm text-center">💳 Cards</div>
          </div>
          <div className="text-xs text-gray-600">
            Templates: Headers, Metrics, Charts, Buttons, Cards, Grids, Progress bars, Alerts
          </div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"/>
            <span>Dynamic UI system ready</span>
          </div>
        </Alert>
      </div>
    )
 },
 {
    id: 'structured_extraction',
    name: 'Structured Data Extraction',
    description: 'Extract structured data from documents using Pydantic schemas',
    category: 'Output',
    icon: Database,
    demo: () => (
      <div className="space-y-4">
        <div className="border rounded-lg p-3">
          <div className="font-medium mb-2">Extraction Capabilities</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-blue-100 p-2 rounded text-sm text-center">📄 Contract Analysis</div>
            <div className="bg-green-100 p-2 rounded text-sm text-center">📊 Financial Data</div>
            <div className="bg-purple-100 p-2 rounded text-sm text-center">📋 Forms & Invoices</div>
            <div className="bg-yellow-100 p-2 rounded text-sm text-center">🔍 Entity Extraction</div>
          </div>
          <div className="text-xs text-gray-600">
            Uses Pydantic schemas to extract and validate structured information from unstructured documents
          </div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
            <span>Extraction engine ready (Gemini Flash 2.5)</span>
          </div>
        </Alert>
      </div>
    ),
    configFields: [
      {
        key: 'pydantic_file',
        label: 'Extraction Schema',
        type: 'select',
        placeholder: 'Select extraction schema',
        options: [
          { 
            label: 'Contract (Nordic Solar)', 
            value: 'contract_extraction_schema',
            tags: ['ONE', 'admin-tools']
          },
          { label: 'Contract (Generic)', value: 'generic_contract_schema' },
          { label: 'Invoice', value: 'invoice_extraction_schema' },
          { label: 'Meeting Minutes', value: 'meeting_minutes_schema' },
          { label: 'Financial Statement', value: 'financial_statement_schema' },
          { label: 'Custom JSON Schema', value: 'custom' }
        ],
        required: true,
        defaultValue: 'generic_contract_schema'
      },
      {
        key: 'schema',
        label: 'Custom JSON Schema (only if Custom JSON Schema selected above)',
        type: 'text',
        placeholder: '{\n  "type": "object",\n  "properties": {\n    "field1": {\n      "type": "string",\n      "description": "Description for field1"\n    }\n  },\n  "required": ["field1"]\n}',
        required: false
      }
    ] as TagAwareConfigField[]
 },
 {
    id: 'artifacts',
    name: 'Interactive Artifacts',
    description: 'Generate interactive code components, documents, and applications',
    category: 'Output',
    icon: BrainCircuit,
    demo: () => (
      <div className="space-y-4">
        <div className="border rounded-lg p-3">
          <div className="font-medium mb-2">Artifact Examples</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-green-100 p-2 rounded text-sm text-center">⚛️ React Components</div>
            <div className="bg-blue-100 p-2 rounded text-sm text-center">📄 HTML Pages</div>
            <div className="bg-yellow-100 p-2 rounded text-sm text-center">⚡ JavaScript Apps</div>
            <div className="bg-purple-100 p-2 rounded text-sm text-center">📋 Documents</div>
          </div>
          <div className="text-xs text-gray-600">
            Creates interactive components with live preview and editing capabilities
          </div>
        </div>
        <Alert>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
            <span>Artifact system ready</span>
          </div>
        </Alert>
      </div>
    )
 },

];

// Categories will be computed dynamically based on filtered tools

export default function ToolSelector({ 
    selectedTools, 
    onChange,
    toolConfigs = {},
    onConfigChange,
    defaultTools = ['preview','highlights', 'plots', 'alerts', 'tooltips'], // Default tools that should be on
    defaultOpen = false, // Start collapsed
    availableAssistants = [] // Optional assistants list
  }: ToolSelectorProps) {
    const [activeDemoId, setActiveDemoId] = React.useState('');
    const [filter, setFilter] = React.useState('All');
    const [isOpen, setIsOpen] = React.useState(defaultOpen);
    const [availableTags, setAvailableTags] = React.useState<Tag[]>([]);
    const [filteredTools, setFilteredTools] = React.useState(AVAILABLE_TOOLS);
    const { toast } = useToast();
    
    // Get current user for tag access checking
    const [user, setUser] = React.useState(getAuth().currentUser);
    
    // Listen for auth state changes
    React.useEffect(() => {
      const auth = getAuth();
      const unsubscribe = auth.onAuthStateChanged((user) => {
        setUser(user);
      });
      return unsubscribe;
    }, []);

    // Load available tags when component mounts
    const [tagsLoaded, setTagsLoaded] = React.useState(false);
    React.useEffect(() => {
      const loadTags = async () => {
        try {
          const tags = await FirebaseService.getAllTags();
          setAvailableTags(tags);
          setTagsLoaded(true);
        } catch (error) {
          console.error('Failed to load tags:', error);
          setTagsLoaded(true); // Set as loaded even on error
        }
      };
      
      loadTags();
    }, []);

    // Filter tools based on tag access
    React.useEffect(() => {
      const filterToolsByAccess = () => {
        const filtered = AVAILABLE_TOOLS.filter(tool => {
          // If tool has no tags, it's available to everyone
          if (!tool.tags || tool.tags.length === 0) {
            return true;
          }
          
          // Check if user has access to at least one of the tool's tags
          return checkTagAccess(tool.tags, availableTags, user);
        });
        
        setFilteredTools(filtered);
        
        // Update active demo if current one is no longer available or if not set
        if (!activeDemoId || !filtered.find(tool => tool.id === activeDemoId)) {
          setActiveDemoId(filtered[0]?.id || '');
        }
      };

      filterToolsByAccess();
    }, [availableTags, user, activeDemoId]);

    // Initialize with defaults if no selections yet
    React.useEffect(() => {
        if (selectedTools.length === 0 && defaultTools.length > 0) {
        onChange(defaultTools);
        }
    }, [selectedTools, defaultTools, onChange]);

    const handleConfigUpdate = (toolId: string, config: Record<string, any>) => {
      onConfigChange?.(toolId, config);
      
      // Check if this is a search tool that just got a hardcode-query and no-first-impression is enabled
      const conditionallyIncompatibleWithNoFirstImpression = [
        'google_search_retrieval',
        'vertex_search',
        'vertex_search_first'
      ];
      
      if (selectedTools.includes('no-first-impression') && 
          conditionallyIncompatibleWithNoFirstImpression.includes(toolId) &&
          config['hardcode-query'] && config['hardcode-query'].trim() !== '') {
        
        // Tool is now compatible with no-first-impression, so we can re-enable it
        if (!selectedTools.includes(toolId)) {
          const newTools = [...selectedTools, toolId];
          onChange(newTools);
        }
      }
    };

    // Check if a tool has all required configuration fields filled
    const isToolConfigured = (toolId: string): boolean => {
      const tool = AVAILABLE_TOOLS.find(t => t.id === toolId);
      if (!tool?.configFields) return true; // No config required
      
      const config = toolConfigs[toolId] || {};
      
      return tool.configFields
        .filter(field => field.required)
        .every(field => {
          const value = config[field.key];
          if (field.type === 'checkbox') return true; // Checkboxes are always valid
          if (field.key === 'customBucketUrl') {
            // Custom bucket URL is only required if bucketUrl is 'custom'
            return config.bucketUrl !== 'custom' || (value && value.trim() !== '');
          }
          if (field.key === 'customConfigId') {
            // Custom config ID is only required if configId is 'custom'
            return config.configId !== 'custom' || (value && value.trim() !== '');
          }
          if (field.key === 'customDatastoreId') {
            // Custom datastore ID is only required if datastore_id is 'custom'
            return config.datastore_id !== 'custom' || (value && value.trim() !== '');
          }
          if (field.key === 'bucketUrl' && tool.id === 'document_search_agent') {
            // Bucket URL is only required if preset_config is 'custom'
            return config.preset_config !== 'custom' || (value && value.trim() !== '');
          }
          if (field.key === 'datastore_id' && tool.id === 'document_search_agent') {
            // Datastore ID is only required if preset_config is 'custom'
            return config.preset_config !== 'custom' || (value && value.trim() !== '');
          }
          // For document_search_agent, preset_config is always required and sufficient
          if (field.key === 'preset_config' && tool.id === 'document_search_agent') {
            return value && value.trim() !== '';
          }
          return value && value.trim() !== '';
        });
    };

    // Tools that require configuration
    const toolsRequiringConfig = ['vertex_search', 'file-browser', 'vertex_search_app', 'vertex_search_first', 'document_search_agent', 'aitana-agent'];

    // Check if a search tool is compatible with no-first-impression
    const isSearchToolCompatibleWithNoFirstImpression = (toolId: string): boolean => {
      const searchToolsWithHardcodeQuery = ['google_search_retrieval', 'vertex_search', 'vertex_search_first'];
      
      if (!searchToolsWithHardcodeQuery.includes(toolId)) {
        return true; // Not a search tool, so compatibility doesn't apply
      }
      
      const config = toolConfigs[toolId] || {};
      const hasHardcodedQuery = config['hardcode-query'] && config['hardcode-query'].trim() !== '';
      
      return hasHardcodedQuery; // Compatible if it has a hardcoded query
    };

    const toggleTool = (toolId: string) => {
      // Define tools that are always incompatible with no-first-impression
      const alwaysIncompatibleWithNoFirstImpression = [
        'user-history',
        'domain-history', 
        'global-history',
        'code_execution'
      ];
      
      // Search tools that can be compatible if they have hardcode-query
      const conditionallyIncompatibleWithNoFirstImpression = [
        'google_search_retrieval',
        'vertex_search',
        'vertex_search_first'
      ];

      if (selectedTools.includes(toolId)) {
        // Removing a tool
        onChange(selectedTools.filter(id => id !== toolId));
      } else {
        // Adding a tool - check if it requires configuration
        if (toolsRequiringConfig.includes(toolId) && !isToolConfigured(toolId)) {
          // Show toast notification instead of silently failing
          const tool = filteredTools.find(t => t.id === toolId);
          toast({
            title: "Configuration Required",
            description: `Please configure "${tool?.name}" before enabling it. Click the settings icon to configure.`,
            variant: "destructive",
          });
          return;
        }

        // Adding a tool
        let newTools = [...selectedTools, toolId];
        
        // If adding no-first-impression, remove incompatible tools
        if (toolId === 'no-first-impression') {
          newTools = newTools.filter(id => {
            // Always remove always-incompatible tools
            if (alwaysIncompatibleWithNoFirstImpression.includes(id)) {
              return false;
            }
            // Remove conditionally incompatible tools that don't have hardcode-query
            if (conditionallyIncompatibleWithNoFirstImpression.includes(id)) {
              return isSearchToolCompatibleWithNoFirstImpression(id);
            }
            return true;
          });
        }
        
        // If adding a search tool without hardcode-query while no-first-impression is enabled
        if (conditionallyIncompatibleWithNoFirstImpression.includes(toolId) && 
            newTools.includes('no-first-impression') && 
            !isSearchToolCompatibleWithNoFirstImpression(toolId)) {
          newTools = newTools.filter(id => id !== 'no-first-impression');
        }
        
        // If adding an always-incompatible tool, remove no-first-impression
        if (alwaysIncompatibleWithNoFirstImpression.includes(toolId) && newTools.includes('no-first-impression')) {
          newTools = newTools.filter(id => id !== 'no-first-impression');
        }
        
        onChange(newTools);
      }
    };
  
    // Compute categories from filtered tools
    const categories = React.useMemo(() => {
      return Array.from(new Set(filteredTools.map(tool => tool.category)));
    }, [filteredTools]);

    const categoryFilteredTools = React.useMemo(() => {
      return filteredTools.filter(tool => 
        filter === 'All' || tool.category === filter
      );
    }, [filteredTools, filter]);
  
    const ActiveDemo = filteredTools.find(tool => tool.id === activeDemoId)?.demo;

  
    return (
        <Card>
          <div className="border-b">
            <div className="flex items-center gap-2 p-4 pb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
                className="shrink-0"
              >
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              <h3 className="font-semibold shrink-0">Tools & Features</h3>
            </div>
      
            <ScrollArea className="w-full pb-4 px-4">
              <div className="flex flex-wrap gap-1">
                {selectedTools.map(toolId => {
                  const tool = filteredTools.find(t => t.id === toolId);
                  return tool ? (
                    <div key={toolId} className="flex items-center gap-1">
                      <Badge 
                        variant="secondary" 
                        className="h-5 shrink-0"
                        onClick={() => toggleTool(toolId)}
                      >
                        <tool.icon className="h-3 w-3 mr-1" />
                        {tool.name}
                        <span className="ml-1 opacity-60">×</span>
                      </Badge>
                      {/* Display tag badges for this tool */}
                      {tool.tags && tool.tags.length > 0 && (
                        <div className="flex gap-1">
                          {tool.tags.map(tagId => {
                            const tag = availableTags.find(t => t.id === tagId);
                            const tagName = tag?.name || tagId;
                            
                            // Color scheme matching the sidebar user tags
                            const getTagColor = (name: string) => {
                              if (name === 'admin-tools') return 'bg-red-100 text-red-800 border-red-200';
                              if (name === 'beta-testers') return 'bg-purple-100 text-purple-800 border-purple-200';
                              if (name === 'finance-team') return 'bg-green-100 text-green-800 border-green-200';
                              if (name === 'ONE') return 'bg-blue-100 text-blue-800 border-blue-200';
                              return 'bg-gray-100 text-gray-800 border-gray-200';
                            };
                            
                            return (
                              <Badge 
                                key={tagId}
                                variant="outline" 
                                className={`text-xs py-0 px-1 h-5 ${getTagColor(tagName)}`}
                              >
                                {tagName}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null;
                })}
              </div>
            </ScrollArea>
          </div>
  
        <div className={cn(
          "transition-all duration-200",
          !isOpen && "h-0 overflow-hidden"
        )}>
          <CardContent className="p-4">
            {/* Categories */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                <Badge 
                  variant={filter === 'All' ? "default" : "secondary"}
                  className="cursor-pointer"
                  onClick={() => setFilter('All')}
                >
                  All
                </Badge>
                {categories.map(category => (
                  <Badge
                    key={category}
                    variant={filter === category ? "default" : "secondary"}
                    className="cursor-pointer"
                    onClick={() => setFilter(category)}
                  >
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
  
          {/* Available Tools - Full Width */}
          <div className="mb-6">
            <Label className="mb-2 block">Available Tools</Label>
            <Card>
              <ScrollArea className="h-[400px]">
                <div className="pr-3 pb-3">
              {categoryFilteredTools.map((tool) => {
                  // Check if this tool is disabled due to no-first-impression
                  const alwaysIncompatibleWithNoFirstImpression = [
                    'user-history',
                    'domain-history',
                    'global-history',
                    'code_execution'
                  ];
                  
                  const conditionallyIncompatibleWithNoFirstImpression = [
                    'google_search_retrieval',
                    'vertex_search',
                    'vertex_search_first'
                  ];
                  
                  const isAlwaysIncompatible = selectedTools.includes('no-first-impression') && 
                                            alwaysIncompatibleWithNoFirstImpression.includes(tool.id);
                  
                  const isConditionallyIncompatible = selectedTools.includes('no-first-impression') && 
                                                    conditionallyIncompatibleWithNoFirstImpression.includes(tool.id) &&
                                                    !isSearchToolCompatibleWithNoFirstImpression(tool.id);
                  
                  const isIncompatibleDisabled = isAlwaysIncompatible || isConditionallyIncompatible;
                  
                  // Check if this tool requires configuration but isn't configured
                  const isSelected = selectedTools.includes(tool.id);
                  const requiresConfig = toolsRequiringConfig.includes(tool.id);
                  const isConfigured = isToolConfigured(tool.id);
                  const isConfigDisabled = requiresConfig && !isConfigured && !isSelected;
                  
                  const isDisabled = isIncompatibleDisabled || isConfigDisabled;
                  
                  return (
                    <div
                      key={tool.id}
                      className={cn(
                        "flex flex-col p-3 gap-3 cursor-pointer",
                        !isDisabled && "hover:bg-accent",
                        activeDemoId === tool.id && !isDisabled && "bg-accent",
                        // Remove the opacity change for config disabled state
                        isIncompatibleDisabled && "opacity-50 cursor-not-allowed"
                      )}
                      onClick={() => !isIncompatibleDisabled && setActiveDemoId(tool.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className={cn(
                            "w-4 h-4 mt-1 rounded border flex items-center justify-center",
                            selectedTools.includes(tool.id) 
                              ? "bg-primary border-primary" 
                              : isDisabled
                              ? "border-gray-200 bg-gray-100"
                              : "border-gray-300",
                            !isDisabled && "cursor-pointer"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                              toggleTool(tool.id);
                            }
                          }}
                        >
                          {selectedTools.includes(tool.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={cn(
                            "font-medium text-sm flex items-center gap-2 flex-wrap",
                            // Only apply opacity to the tool name/icon when config is missing
                            isConfigDisabled && "opacity-50"
                          )}>
                            <tool.icon className="h-4 w-4" />
                            <span className="break-words">{tool.name}</span>
                            {/* Display tag badges for this tool */}
                            {tool.tags && tool.tags.length > 0 && (
                              <div className="flex gap-1 ml-1">
                                {tool.tags.map(tagId => {
                                  const tag = availableTags.find(t => t.id === tagId);
                                  const tagName = tag?.name || tagId;
                                  
                                  // Color scheme matching the sidebar user tags
                                  const getTagColor = (name: string) => {
                                    if (name === 'admin-tools') return 'bg-red-100 text-red-800 border-red-200';
                                    if (name === 'beta-testers') return 'bg-purple-100 text-purple-800 border-purple-200';
                                    if (name === 'finance-team') return 'bg-green-100 text-green-800 border-green-200';
                                    if (name === 'ONE') return 'bg-blue-100 text-blue-800 border-blue-200';
                                    return 'bg-gray-100 text-gray-800 border-gray-200';
                                  };
                                  
                                  return (
                                    <Badge 
                                      key={tagId}
                                      variant="outline" 
                                      className={`text-xs py-0 px-1 h-4 ${getTagColor(tagName)}`}
                                    >
                                      {tagName}
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground break-words">
                            {isAlwaysIncompatible 
                              ? "Incompatible with 'Suppress First Impression' tool"
                              : isConditionallyIncompatible
                              ? "Configure 'Hardcoded Query' to use with 'Suppress First Impression'"
                              : tool.description}
                          </div>
                        </div>
                      </div>
                    
                    {/* Add configuration UI for tools with config fields (always show for required configs) */}
                    {tool.configFields && (toolsRequiringConfig.includes(tool.id) || selectedTools.includes(tool.id)) && (
                      <ToolConfigurator
                        toolId={tool.id}
                        currentConfig={toolConfigs[tool.id]}
                        onConfigUpdate={handleConfigUpdate}
                        configFields={filterConfigFieldsByTags(
                          tool.configFields as TagAwareConfigField[],
                          availableTags,
                          user
                        )}
                        availableTags={availableTags}
                        availableAssistants={availableAssistants}
                        onToolEnable={(toolId) => {
                          // Auto-enable the tool after configuration
                          if (!selectedTools.includes(toolId)) {
                            onChange([...selectedTools, toolId]);
                          }
                        }}
                      />
                    )}
                  </div>
                  );
                })}
                  </div>
                </ScrollArea>
              </Card>
            </div>
  
            {/* Demo Preview - Full Width Below */}
            <div>
              <Label className="mb-2 block">Demo Preview</Label>
              <Card>
                <ScrollArea className="h-[300px]">
                  <CardContent className="p-3">
                    {ActiveDemo && <ActiveDemo />}
                  </CardContent>
                </ScrollArea>
              </Card>
            </div>
          </CardContent>
        </div>
      </Card>
    );
  }